import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import DataTable from '../../components/common/DataTable/DataTable';
import { usePermission } from '../../hooks/usePermissions';
import styles from './IndicesControl.module.css';
import { formatISTDateTime } from '../../utils/datetime';

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const GET_INDICES_CONTROL = gql`
  query GetIndicesControl($page: Int!, $limit: Int!, $search: String, $sortBy: String, $sortOrder: String, $type: String, $group_name: String, $exchange: String, $is_active: Boolean, $fromDate: String, $toDate: String, $group_token: String ) {
    getIndicesControl(page: $page, limit: $limit, search: $search, sortBy: $sortBy, sortOrder: $sortOrder, type: $type, group_name: $group_name, exchange: $exchange, is_active: $is_active, fromDate: $fromDate, toDate: $toDate, group_token: $group_token) {
      items {
        id
        type
        group_name
        group_token
        exchange
        symbol
        token
        is_active
        created_at
        updated_at
      }
      totalCount
    }
  }
`;

const GET_MASTER_DATA = gql`
  query GetMasterData($exch_seg: String) {
    getAllIndicesTypes {
        unique_id
        indice_name
    }
    getAllIndicesGroups {
        unique_id
        indices_group_name
    }
    getAllInternalSymbols(exch_seg: $exch_seg) {
        token
        symbol
    }
    getExchanges {
        exchange_code
        exchange_name
    }
    getAllSectors {
        id
        sector_name
    }
  }
`;

const GET_GROUPS_BY_TYPE = gql`
  query GetGroupsByType($type: String!, $exchange: String) {
     getAllIndicesGroupsByType(type: $type, exchange: $exchange) {
        id
        name
        token
        instrumenttype
        exch_seg
    }
  }
`;


const ADD_INDEX_CONTROL = gql`
  mutation AddIndexControl($type: String!, $group_name: String!, $group_token: String!, $exchange: String!, $symbol: String!, $token: String, $is_active: Boolean) {
    addIndexControl(type: $type, group_name: $group_name, group_token: $group_token, exchange: $exchange, symbol: $symbol, token: $token, is_active: $is_active) {
      id
      symbol
    }
  }
`;

const UPDATE_INDEX_CONTROL = gql`
  mutation UpdateIndexControl($id: ID!, $type: String, $group_name: String,$group_token: String, $exchange: String, $symbol: String, $token: String, $is_active: Boolean) {
    updateIndexControl(id: $id, type: $type, group_name: $group_name, group_token: $group_token, exchange: $exchange, symbol: $symbol, token: $token, is_active: $is_active) {
      id
      symbol
    }
  }
`;

const DELETE_INDEX_CONTROL = gql`
  mutation DeleteIndexControl($id: ID!) {
    deleteIndexControl(id: $id)
  }
`;

const IndicesControl = () => {
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('group_name');
    const [sortOrder, setSortOrder] = useState('ASC');

    const { hasPermission } = usePermission()

    // Filters
    const [filterType, setFilterType] = useState('');
    const [filterGroup, setFilterGroup] = useState('');
    const [filterExchange, setFilterExchange] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    // const [formData, setFormData] = useState({
    //     id: '',
    //     type: '',
    //     group_name: '',
    //     exchange: 'NSE',
    //     symbol: '',
    //     is_active: true
    // });

    const [formData, setFormData] = useState({
        id: '',
        type: '',
        group_name: '',
        group_token: '',
        exchange: '',
        token: '',
        symbol: '',
        is_active: true
    });

    const LIMIT = 10;

    const { loading, error, data, refetch } = useQuery(GET_INDICES_CONTROL, {
        variables: {
            page,
            limit: LIMIT,
            search: searchQuery,
            sortBy,
            sortOrder,
            type: filterType || undefined,
            group_name: filterGroup || undefined,
            exchange: filterExchange || undefined,
            is_active: filterActive === '' ? undefined : filterActive === 'true',
            fromDate: fromDate || undefined,
            toDate: toDate || undefined
        },
        fetchPolicy: 'network-only'
    });

    const { data: masterData } = useQuery(GET_MASTER_DATA, {
        variables: { exch_seg: formData.exchange }
    });

    const { data: groupDataByType } = useQuery(GET_GROUPS_BY_TYPE,
        {
            variables: {
                type: 'AMXIDX',
                exchange: formData.exchange
            },
        }
    )

    const [addIndexControl] = useMutation(ADD_INDEX_CONTROL);
    const [updateIndexControl] = useMutation(UPDATE_INDEX_CONTROL);
    const [deleteIndexControl] = useMutation(DELETE_INDEX_CONTROL);

    // Derived state
    const items = data?.getIndicesControl?.items || [];
    const totalCount = data?.getIndicesControl?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / LIMIT);

    const handleNext = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const handlePrevious = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleSearch = () => {
        if (searchInput) {
            setSearchQuery(searchInput);
            setPage(1);
        }
    };

    const handleSort = (columnKey) => {
        if (sortBy === columnKey) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(columnKey);
            setSortOrder('ASC');
        }
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilterType('');
        setFilterGroup('');
        setFilterExchange('');
        setFilterActive('');
        setFromDate('');
        setToDate('');
        setSearchInput('');
        setSearchQuery('');
        setPage(1);
    };

    // Local state for forms
    const [showForm, setShowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // const handleSelectChange = (name, value) => {
    //     // setFormData({
    //     //     ...formData,
    //     //     [name]: value
    //     // });
    //     setFormData(prev => {
    //         const updated = {
    //             ...prev,
    //             [name]: value
    //         };

    //         // Reset dependent dropdown
    //         if (name === "type") {
    //             updated.group_name = ""; // reset group when type changes
    //         }

    //         return updated;
    //     });
    // };

    const handleSelectChange = (name, value) => {
        console.log(`value value`)
        // If value is an object (from SearchableDropdown)
        if (typeof value === "object") {
            if (name === "token") {
                setFormData(prev => ({
                    ...prev,
                    token: value.value,
                    symbol: value.symbol
                }));
                return;
            } else if (name === "group_name") {
                setFormData(prev => ({
                    ...prev,
                    group_name: value.name,
                    group_token: value.value,
                    type: value.name, // Force synchronization
                }));
                return;
            }
        }

        // fallback for normal values
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === "type") updated.group_name = "";
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editMode) {
                await updateIndexControl({ variables: { ...formData, id: formData.id } });
                alert('Index Control updated successfully');
            } else {
                await addIndexControl({ variables: { ...formData } });
                alert('Index Control added successfully');
            }
            setShowForm(false);
            resetForm();
            refetch();
        } catch (error) {
            console.error('Error saving index control:', error);
            alert('Failed to save index control');
        }
    };

    const resetForm = () => {
        setFormData({
            id: '',
            type: '',
            group_name: '',
            group_token: '',
            exchange: '',
            symbol: '',
            token: '',
            is_active: true
        });
        setEditMode(false);
    };

    const handleEdit = (item) => {
        setFormData({
            id: item.id || '',
            type: item.group_name || item.type || '', // Prefer group_name for display consistency
            group_name: item.group_name || '',
            group_token: item.group_token || '',
            token: item.token || '',
            exchange: item.exchange || '',
            symbol: item.symbol || '',
            is_active: item.is_active ?? true
        });
        setEditMode(true);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                await deleteIndexControl({ variables: { id } });
                alert('Item deleted successfully');
                refetch();
            } catch (error) {
                console.error('Error deleting item:', error);
                alert('Failed to delete item');
            }
        }
    };

    const columns = [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'type', label: 'Display Name', sortable: true },
        { key: 'group_name', label: 'Group Name', sortable: true },
        { key: 'group_token', label: 'Group Token', sortable: true },
        { key: 'exchange', label: 'Exchange', sortable: true },
        { key: 'symbol', label: 'Symbol', sortable: true },
        { key: 'token', label: 'Token', sortable: true },
        {
            key: 'is_active',
            label: 'Active',
            sortable: true,
            render: (val) => val ? 'Yes' : 'No'
        },
        {
            key: 'created_at',
            label: 'Created At',
            sortable: true,
            render: (val) => formatISTDateTime(Number(val) || val)
        },
        {
            key: 'updated_at',
            label: 'Updated At',
            sortable: true,
            render: (val) => formatISTDateTime(Number(val) || val)
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className={styles.flexCenterGap8}>
                    {hasPermission("MODIFY_INTERNALTOKEN_INDICESCONTROL") && (
                        <button className={styles.iconBtn} onClick={() => handleEdit(row)} title="Edit Item">
                            <EditIcon />
                        </button>
                    )}
                    {hasPermission("DELETE_INTERNALTOKEN_INDICESCONTROL") && (
                        <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(row.id)} title="Delete Item">
                            <TrashIcon />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <h2>Indices Control</h2>
                    <p>Manage application indices and configurations</p>
                </div>
                
                <div className={styles.headerRight}>
                    <div className={styles.searchBox}>
                        <span className={styles.searchIcon}>
                            <SearchIcon />
                        </span>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search indices..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </div>
                    {hasPermission("CREATE_INTERNALTOKEN_INDICESCONTROL") && (
                        <button
                            className={styles.primaryBtn}
                            onClick={() => { setShowForm(true); resetForm(); }}
                        >
                            + Add Item
                        </button>
                    )}
                </div>
            </div>

            {/* Advanced Filters */}
            <div className={styles.filtersWrapper}>
                {/* <span className={styles.filterLabel}>Filters:</span> */}

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>From:</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>To:</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Display Name:</label>
                    <select
                        value={filterType}
                        onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        {masterData?.getAllIndicesTypes?.map((t) => (
                            <option key={t.unique_id} value={t.indice_name}>{t.indice_name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Group:</label>
                    <select
                        value={filterGroup}
                        onChange={(e) => { setFilterGroup(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        {masterData?.getAllIndicesGroups?.map((g) => (
                            <option key={g.unique_id} value={g.indices_group_name}>{g.indices_group_name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Exch:</label>
                    <select
                        value={filterExchange}
                        onChange={(e) => { setFilterExchange(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        {masterData?.getExchanges?.map((ex) => (
                            <option key={ex.exchange_code} value={ex.exchange_code}>{ex.exchange_code}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Active:</label>
                    <select
                        value={filterActive}
                        onChange={(e) => { setFilterActive(e.target.value); setPage(1); }}
                        className={styles.filterInput}
                    >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>

                <button
                    onClick={handleClearFilters}
                    className={styles.clearFiltersBtn}
                >
                    Clear
                </button>
            </div>

            {showForm && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>{editMode ? 'Edit Item' : 'Add New Item'}</h3>
                            <button onClick={() => setShowForm(false)} className={styles.modalCloseBtn}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit} className={styles.formGrid}>
                            <div>
                                <label className={styles.formLabel}>Exchange:</label>
                                <select name="exchange" value={formData.exchange} onChange={handleInputChange} className={styles.formInput}>
                                    {masterData?.getExchanges?.map((ex) => (
                                        <option key={ex.exchange_code} value={ex.exchange_code}>{ex.exchange_code}</option>
                                    ))}
                                </select>
                            </div>
                           
                            <div>
                                <label className={styles.formLabel}>Group Name:</label>
                                <SearchableDropdown
                                    options={groupDataByType?.getAllIndicesGroupsByType?.map((s) => ({
                                        label: s.name,
                                        value: s.token,
                                        name: s.name,
                                        instrumenttype: s.instrumenttype,
                                        exch_seg: s.exch_seg
                                    })) || []}
                                    value={
                                        formData.group_name
                                            ? {
                                                label: formData.group_name || '',
                                                value: formData.group_token || '',
                                                name: formData.group_name || '',
                                                instrumenttype: formData.type,
                                                exch_seg: formData.exchange
                                            }
                                            : null
                                    }
                                    onChange={(val) => handleSelectChange('group_name', val)}
                                    placeholder="Select Group"
                                />
                            </div>

                            <div>
                                <label className={styles.formLabel}>Group Token:</label>
                                <input
                                    type="text"
                                    name="group_token"
                                    value={formData.group_token}
                                    readOnly
                                    className={styles.formInputDisabled}
                                />
                            </div>

                            <div>
                                <label className={styles.formLabel}>Symbol name:</label>
                                <SearchableDropdown
                                    options={masterData?.getAllInternalSymbols?.map((s) => ({
                                        label: `${s.token} (${s.symbol})`,
                                        value: s.token,
                                        symbol: s.symbol
                                    })) || []}
                                    value={
                                        formData.symbol
                                            ? { label: `${formData.token} (${formData.symbol})`, value: formData.token, symbol: formData.symbol }
                                            : null
                                    }
                                    onChange={(val) => handleSelectChange("token", val)}
                                    placeholder="Select Parent Symbol"
                                />
                            </div>

                            <div>
                                <label className={styles.formLabel}>Symbol Token:</label>
                                <input
                                    type="text"
                                    name="token"
                                    value={formData.token}
                                    readOnly
                                    className={styles.formInputDisabled}
                                />
                            </div>

                            
                            <div className={styles.checkboxGroup}>
                                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className={styles.checkboxInput} />
                                <label>Is Active</label>
                            </div>
                            

                            <div className={styles.formActions}>
                                <button type="button" onClick={() => setShowForm(false)} className={styles.btnCancel}>Cancel</button>
                                <button type="submit" className={styles.btnSave}>Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DataTable
                columns={columns}
                data={items}
                loading={loading}
                error={error}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
            />

            {/* Pagination */}
            <div className={styles.imTableFooter}>
                <span>Showing {items.length} records (Total: {totalCount})</span>
                {totalPages > 1 && (
                    <div className={styles.imPagination}>
                        <button 
                            className={styles.imPageBtn} 
                            disabled={page === 1 || loading} 
                            onClick={handlePrevious}
                        >
                            {"<"}
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                                return (
                                    <button 
                                        key={p} 
                                        className={`${styles.imPageBtn} ${page === p ? styles.active : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }
                            if (p === page - 2 || p === page + 2) {
                                return <span key={p} className={styles.paginationDots}>...</span>;
                            }
                            return null;
                        })}

                        <button 
                            className={styles.imPageBtn} 
                            disabled={page >= totalPages || loading} 
                            onClick={handleNext}
                        >
                            {">"}
                        </button>
                    </div>
                )}
                <span className={styles.pageCountInfo}>Page {page} of {totalPages || 1}</span>
            </div>
        </div>
    );
};

const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    // Initialize search term when value changes (e.g. edit mode)
    React.useEffect(() => {
        if (!value) {
            setSearchTerm('');
        } else if (typeof value === 'string') {
            setSearchTerm(value);
        } else if (typeof value === 'object') {
            setSearchTerm(value.label || ''); // extract string from object
        }
    }, [value]);

    // const filteredOptions = options.filter(opt =>
    //     opt.toLowerCase().includes(searchTerm.toLowerCase())
    // );

    const filteredOptions = options.filter(opt => {
        const label = typeof opt === "string" ? opt : opt.label;
        const term = typeof searchTerm === "string" ? searchTerm : '';
        return label.toLowerCase().includes(term.toLowerCase());
    });

    // const handleSelect = (opt) => {
    //     onChange(opt);
    //     setSearchTerm(opt);
    //     setIsOpen(false);
    // };

    const handleSelect = (opt) => {
        const label = typeof opt === "string" ? opt : opt.label;

        onChange(opt);
        setSearchTerm(label);
        setIsOpen(false);
    };

    return (
        <div className={styles.autocompleteContainer}>
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsOpen(true);
                    if (e.target.value === '') onChange(''); // Clear value if cleared
                }}
                onFocus={() => setIsOpen(true)}
                placeholder={placeholder}
                className={styles.formInput}
            />
            {isOpen && filteredOptions.length > 0 && (
                <ul className={styles.autocompleteListDropdown}>
                    {/* {filteredOptions.map((opt, idx) => (
                        <li
                            key={idx}
                            onClick={() => handleSelect(opt)}
                            className={styles.autocompleteItem}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                            {opt}
                        </li>
                    ))} */}
                    {filteredOptions.map((opt, idx) => {
                        const label = typeof opt === "string" ? opt : opt.label;

                        return (
                            <li
                                key={idx}
                                onClick={() => handleSelect(opt)}
                                className={styles.autocompleteItem}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                                {label}
                            </li>
                        );
                    })}
                </ul>
            )}
            {isOpen && filteredOptions.length === 0 && searchTerm && (
                <div className={styles.autocompleteNoOptions}>
                    No options found
                </div>
            )}
            {/* Overlay to close dropdown when clicking outside */}
            {isOpen && (
                <div
                    className={styles.fullscreenOverlay}
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default IndicesControl;